import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useDebounce } from 'usehooks-ts';
import { MdSearch } from 'react-icons/md';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { Pagination } from '@tailwindComponents/Pagination/Pagination';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { BillingCycle } from './invoiceTypes';
import { DEBOUNCE_TIME_IN_MS, INVOICE_LIST } from '../../constants';
import { useAuthHttp } from '../../hooks';
import { UserRoleService } from '../../services';
import { useAuth } from '../../context';
import { getBillingCycleCost, getBillingCycleVatCost } from './invoiceHelper';
import {
  getLastItemSort,
  updateLastItemSorts,
} from '../../utils/pagination';

export function InvoiceList() {
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();

  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [lastItemSorts, setLastItemSorts] = useState<object[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME_IN_MS);

  const fetchInvoices = async (isGoingNext?: boolean) => {
    setIsInvoicesLoading(true);
    try {
      const { data } = await authHttp.post(
        INVOICE_LIST,
        {
          statuses: isWarehouseClient(user) ? ['Finalised', 'Paid'] : '',
          searchQuery: debouncedSearchQuery || undefined,
          pagination: {
            itemsPerPage,
            lastItemSort: getLastItemSort(isGoingNext, lastItemSorts),
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data) {
        setBillingCycles(data.billingCycles);
        setTotalCount(data.totalCount);
        if (data.lastItemSort) {
          updateLastItemSorts(isGoingNext, setLastItemSorts, data.lastItemSort);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsInvoicesLoading(false);
    }
  };

  // Fetch on mount and when debounced search changes
  useEffect(() => {
    setPage(1);
    setLastItemSorts([]);
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const handlePageChange = (newPage: number) => {
    if (newPage === page + 1) {
      fetchInvoices(true);
      setPage(newPage);
    } else if (newPage === page - 1) {
      fetchInvoices(false);
      setPage(newPage);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getInvoiceStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      Pending:
        'bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20',
      Delivered:
        'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20',
      Approved: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10',
      Completed:
        'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
      Cancelled: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20',
      Overdue: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20',
      Finalised:
        'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-700/10',
      Paid: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
          statusColors[status] ||
          'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}
      >
        {status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string, paidAt?: string) => {
    if (status === 'Paid' || paidAt) {
      return (
        <div className='flex flex-col items-center'>
          <span className='inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'>
            Paid
          </span>
          {paidAt && (
            <span className='text-xs text-gray-500 mt-1'>
              {moment(paidAt).format('DD MMM YYYY')}
            </span>
          )}
        </div>
      );
    }
    return (
      <span className='inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20'>
        Unpaid
      </span>
    );
  };

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Invoices', href: '/invoices', current: true },
  ];

  return (
    <>
      <PageHeader header='Invoices' pages={pages} />
      <main className='flex flex-col gap-6 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8'>
        {/* Summary Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          {/* Total Invoices */}
          <div className='bg-white rounded-lg border border-gray-200 p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500'>Total Invoices</p>
                <p className='text-2xl font-semibold text-gray-900 mt-1'>
                  {totalCount}
                </p>
              </div>
              <div className='p-2 bg-gray-100 rounded-lg'>
                <HiOutlineDocumentText className='w-6 h-6 text-gray-500' />
              </div>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
          {/* Search/Filters Row */}
          <div className='flex items-center gap-4 p-4 border-b border-gray-200'>
            {/* Search input */}
            <div className='relative flex-1 max-w-sm'>
              <MdSearch className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='Search invoices...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
            </div>
          </div>

          {/* Table */}
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-gray-200 bg-gray-50'>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Invoice status
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Due date
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    ID
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Date issued
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {isWarehouseClient(user) ? 'Provider' : 'Customer'}
                  </th>
                  <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Amount
                  </th>
                  <th className='px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Payment status
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {isInvoicesLoading && (
                  <tr>
                    <td colSpan={7} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <FloxSpinner size='md' />
                        <p className='text-gray-500 text-sm'>
                          Loading invoices...
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {billingCycles.length === 0 && !isInvoicesLoading && (
                  <tr>
                    <td colSpan={7} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <HiOutlineDocumentText className='w-12 h-12 text-gray-300' />
                        <p className='text-gray-900 font-medium'>
                          No invoices found
                        </p>
                        <p className='text-gray-500 text-sm'>
                          Invoices will appear here once billing cycles are
                          generated
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {billingCycles.map((billingCycle) => {
                  const invoice = isWarehouseClient(user)
                    ? billingCycle.floxToShipperInvoice
                    : billingCycle.lspToFloxInvoice;

                  const subTotalAmount = getBillingCycleCost(billingCycle);
                  const subTotalVatAmount =
                    getBillingCycleVatCost(billingCycle);
                  const totalAmount = subTotalAmount + subTotalVatAmount;

                  const issueDate = new Date(invoice.createdAt);
                  const isOverdue =
                    invoice.status !== 'Paid' &&
                    !invoice.paidAt &&
                    invoice.dueDate &&
                    moment(invoice.dueDate).isBefore(moment());

                  return (
                    <tr
                      key={invoice.id}
                      className='hover:bg-gray-50 cursor-pointer'
                      onClick={() => {
                        navigate(`/invoice/${billingCycle.id}`);
                      }}
                    >
                      {/* Invoice Status */}
                      <td className='px-4 py-4'>
                        {getInvoiceStatusBadge(
                          isOverdue ? 'Overdue' : invoice.status
                        )}
                      </td>

                      {/* Due Date */}
                      <td className='px-4 py-4'>
                        <span
                          className={`text-sm ${
                            isOverdue
                              ? 'text-red-600 font-medium'
                              : 'text-gray-900'
                          }`}
                        >
                          {invoice.dueDate
                            ? moment(invoice.dueDate).format('D MMM YYYY')
                            : 'Unset'}
                        </span>
                      </td>

                      {/* Invoice ID */}
                      <td className='px-4 py-4'>
                        <span className='text-sm font-medium text-blue-600 hover:text-blue-800'>
                          {invoice.invoiceNumber}
                        </span>
                      </td>

                      {/* Date Issued */}
                      <td className='px-4 py-4'>
                        <span className='text-sm text-gray-900'>
                          {moment(issueDate).format('D MMM YYYY')}
                        </span>
                      </td>

                      {/* Provider/Customer */}
                      <td className='px-4 py-4'>
                        <span className='text-sm text-gray-900'>
                          {isWarehouseClient(user)
                            ? billingCycle.lspBusinessName
                            : billingCycle.shipperBusinessName}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className='px-4 py-4 text-right'>
                        <span className='text-sm font-medium text-gray-900'>
                          {invoice.lines[0]?.currencySymbol}
                          {totalAmount.toFixed(2)}
                        </span>
                      </td>

                      {/* Payment Status */}
                      <td className='px-4 py-4 text-center'>
                        {getPaymentStatusBadge(invoice.status, invoice.paidAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {totalCount > 0 && (
            <div className='px-4 py-3 border-t border-gray-200 flex justify-end'>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalCount}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
